import { useMemo } from 'react';
import * as fmt from '../lib/format.js';
import { toISODay, type DayRow } from '../lib/stats.js';

interface Props {
  series: DayRow[];
  /**
   * Dernier jour ouvert à la saisie, demain compris. Au-delà, la case reste
   * affichée mais inerte : un clic qui n'ouvrait rien passait pour une panne.
   */
  maxDay: string;
  /** Premier jour du mois affiché. */
  month: Date;
  onMonthChange: (month: Date) => void;
  selectedDay: string | null;
  onSelect: (day: string) => void;
}

/**
 * Largeur de barre : 14 % minimum pour qu'une valeur basse reste visible,
 * le reste proportionnel à la position dans l'amplitude du mois. L'échelle
 * est mensuelle et non globale — on compare des jours entre eux, pas à
 * l'historique complet.
 */
function barWidth(value: number | null, low: number, high: number): string {
  if (value == null) return '0%';
  const ratio = Math.max(0, Math.min(1, (value - low) / (high - low || 1)));
  return `${Math.round(14 + ratio * 86)}%`;
}

export function CalendarCard({
  series,
  maxDay,
  month,
  onMonthChange,
  selectedDay,
  onSelect,
}: Props) {
  const today = toISODay(new Date());

  const { cells, leading } = useMemo(() => {
    const year = month.getFullYear();
    const monthIndex = month.getMonth();
    const first = new Date(year, monthIndex, 1);
    // getDay() place dimanche en 0 ; la semaine française commence lundi.
    const lead = (first.getDay() + 6) % 7;
    const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();

    const byDay = new Map(series.map((row) => [row.day, row]));
    const inMonth = series.filter(
      (row) => row.date.getMonth() === monthIndex && row.date.getFullYear() === year,
    );

    const spread = (key: 'steps' | 'kcal'): [number, number] => {
      const values = inMonth
        .map((row) => row[key])
        .filter((value): value is number => value != null);
      if (values.length === 0) return [0, 1];
      return [Math.min(...values) * 0.94, Math.max(...values) * 1.02];
    };

    const [stepsLow, stepsHigh] = spread('steps');
    const [kcalLow, kcalHigh] = spread('kcal');

    const built = Array.from({ length: daysInMonth }, (_, offset) => {
      const date = new Date(year, monthIndex, offset + 1);
      const day = toISODay(date);
      const row = byDay.get(day) ?? null;
      return {
        day,
        date,
        number: offset + 1,
        row,
        stepsWidth: barWidth(row?.steps ?? null, stepsLow, stepsHigh),
        kcalWidth: barWidth(row?.kcal ?? null, kcalLow, kcalHigh),
      };
    });

    return { cells: built, leading: lead };
  }, [series, month]);

  const shift = (delta: number) =>
    onMonthChange(new Date(month.getFullYear(), month.getMonth() + delta, 1));

  return (
    <section className="card calendar">
      <header className="calendar__head">
        <h2 className="calendar__month">{fmt.monthYear(month)}</h2>
        <div className="calendar__nav">
          <button type="button" className="btn--icon" onClick={() => shift(-1)} aria-label="Mois précédent">
            ‹
          </button>
          <button type="button" className="btn--icon" onClick={() => shift(1)} aria-label="Mois suivant">
            ›
          </button>
        </div>
      </header>

      <div className="calendar__dows" aria-hidden="true">
        {['lun', 'mar', 'mer', 'jeu', 'ven', 'sam', 'dim'].map((label) => (
          <span key={label}>{label}</span>
        ))}
      </div>

      <div className="calendar__grid">
        {Array.from({ length: leading }, (_, index) => (
          <div key={`lead-${index}`} className="day day--filler" aria-hidden="true" />
        ))}

        {cells.map((cell) => {
          const hasWeight = cell.row?.weight != null;
          // Demain est ouvert à la saisie anticipée, le reste du futur non.
          const ahead = cell.day > today && cell.day <= maxDay;
          const classes = ['day'];
          if (cell.row?.gym === true) classes.push('day--gym');
          if (!hasWeight) classes.push('day--empty');
          if (cell.day === selectedDay) classes.push('day--selected');
          if (cell.day === today) classes.push('day--today');
          if (ahead) classes.push('day--ahead');

          return (
            <button
              type="button"
              key={cell.day}
              className={classes.join(' ')}
              disabled={cell.day > maxDay}
              onClick={() => onSelect(cell.day)}
              aria-label={[
                fmt.date(cell.date, { day: 'numeric', month: 'long' }),
                ahead ? 'à préparer' : null,
                hasWeight ? `${fmt.measuredWeight(cell.row?.weight)} kg` : 'aucune pesée',
                cell.row?.steps == null ? null : `${fmt.number(cell.row.steps)} pas la veille`,
                cell.row?.kcal == null ? null : `${fmt.number(cell.row.kcal)} kcal la veille`,
                cell.row?.gym === true ? 'séance la veille' : null,
              ]
                .filter(Boolean)
                .join(' — ')}
              aria-pressed={cell.day === selectedDay}
            >
              <span className="day__num">{cell.number}</span>
              <span className="day__weight">{hasWeight ? fmt.measuredWeight(cell.row?.weight) : ''}</span>
              <span className="day__bars">
                <span className="day__bar day__bar--steps" style={{ width: cell.stepsWidth }} />
                <span className="day__bar day__bar--kcal" style={{ width: cell.kcalWidth }} />
              </span>
            </button>
          );
        })}
      </div>

      <footer className="calendar__legend">
        <span>
          <i className="day__bar--steps" /> Pas (veille)
        </span>
        <span>
          <i className="day__bar--kcal" /> Kcal (veille)
        </span>
        <span>
          <i className="calendar__gym" /> Séance
        </span>
        <span className="spacer">Clic = éditer</span>
      </footer>
    </section>
  );
}
